import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Rocket, Truck, Store, Trash2, Calendar, RefreshCw, Trophy, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import ProgressBar from "../components/ProgressBar";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [votes, setVotes] = useState([]);
  const [preorders, setPreorders] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [newShipDate, setNewShipDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, v, p, lb] = await Promise.all([
      api.getCampaign(id),
      api.listVotes(id),
      api.listPreorders(id),
      api.leaderboard(id).catch(() => []),
    ]);
    setCampaign(c);
    setVotes(v);
    setPreorders(p);
    setLeaderboard(lb);
    setNewShipDate(c.estimatedShipDate ? c.estimatedShipDate.slice(0, 10) : "");
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!campaign) return <div className="text-sm text-gray-500">Loading…</div>;

  const pct = Math.min(100, Math.round((campaign.currentVoteCount / Math.max(1, campaign.voteThreshold)) * 100));

  const onActivate = async () => {
    setBusy(true);
    try {
      const r = await api.activateCampaign(id);
      toast.success(`Pre-order activated. Notified ${r.notifiedVoters} voter(s).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    } finally {
      setBusy(false);
    }
  };

  const onLaunchSale = async () => {
    if (!window.confirm("Launch this product for full-price sale? It will leave the Drop Room and all LaunchPad pages and go live on your store.")) return;
    setBusy(true);
    try {
      const r = await api.launchForSale(id);
      toast.success(`Launched for sale. Notified ${r.notifiedVoters} voter(s).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not launch");
    } finally {
      setBusy(false);
    }
  };

  const onShipDateUpdate = async () => {
    if (!newShipDate) return toast.error("Pick a date");
    setBusy(true);
    try {
      await api.updateCampaign(id, { estimatedShipDate: newShipDate });
      toast.success("Ship date updated. Customers will be emailed.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const onFulfillAll = async () => {
    setBusy(true);
    try {
      const r = await api.fulfillAll(id);
      toast.success(`Fulfilled ${r.fulfilled} order(s).`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fulfill");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this campaign? This removes all supporters and pre-orders.")) return;
    setBusy(true);
    try {
      await api.deleteCampaign(id);
      toast.success("Campaign deleted");
      navigate("/app");
    } catch {
      toast.error("Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      <Link to="/app" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      {/* Header */}
      <div className="lp-card p-6 md:p-8">
        <div className="flex items-start gap-5 flex-wrap">
          {campaign.productImageUrl && (
            <img
              src={campaign.productImageUrl}
              alt={campaign.productTitle}
              className="w-20 h-20 rounded-xl object-cover border border-gray-100"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="lp-section-label">Campaign</div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1.5" data-testid="campaign-title">
              {campaign.productTitle}
            </h1>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <StatusBadge status={campaign.status} testId="campaign-status" />
              <span className="text-xs text-gray-500">
                Created {new Date(campaign.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/storefront/${id}`} className="lp-btn-secondary" data-testid="open-storefront-btn">
              <Store size={14} /> Open storefront
            </Link>
            <button onClick={load} className="lp-btn-secondary" data-testid="refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-7">
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-sm font-medium text-gray-900">
              {campaign.currentVoteCount} / {campaign.voteThreshold} supporters
            </div>
            <div className="text-xs text-gray-500">{pct}% to threshold</div>
          </div>
          <ProgressBar value={campaign.currentVoteCount} max={campaign.voteThreshold} testId="campaign-progress" />
        </div>
      </div>

      {/* Quick info bento */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <InfoCard label="Pre-order price" value={campaign.preOrderPrice ? `$${campaign.preOrderPrice.toFixed(2)}` : "—"} />
        <InfoCard label="Deposit" value={campaign.depositAmount ? `$${campaign.depositAmount.toFixed(2)}` : "Full payment"} />
        <InfoCard label="Ship date" value={campaign.estimatedShipDate ? campaign.estimatedShipDate.slice(0, 10) : "—"} />
        <InfoCard label="Pre-orders" value={preorders.length + (campaign.preOrderCap ? ` / ${campaign.preOrderCap}` : "")} />
      </div>

      {/* Actions */}
      <div className="lp-card p-6">
        <h2 className="text-base font-semibold tracking-tight mb-4">Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionTile
            title="Open pre-order"
            description="Flip to pre-order — takes deposits/full price now, ships later. Notifies voters."
            disabled={campaign.status !== "voting" || busy}
            onClick={onActivate}
            icon={Rocket}
            testId="activate-preorder-btn"
          />
          <ActionTile
            title="Launch for sale"
            description="Go live for full-price sale now. Removes it from LaunchPad and notifies voters."
            disabled={campaign.status !== "voting" || busy}
            onClick={onLaunchSale}
            icon={ShoppingBag}
            testId="launch-sale-btn"
          />

          <div className="lp-card p-5 border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Calendar size={16} />
              <div className="text-sm font-semibold">Update ship date</div>
            </div>
            <p className="text-xs text-gray-500 mb-3">Emails all pre-order customers.</p>
            <input
              type="date"
              className="lp-input mb-3"
              data-testid="new-ship-date-input"
              value={newShipDate}
              onChange={(e) => setNewShipDate(e.target.value)}
            />
            <button
              className="lp-btn-secondary w-full justify-center"
              data-testid="update-ship-date-btn"
              onClick={onShipDateUpdate}
              disabled={busy}
            >
              Update & notify
            </button>
          </div>

          <ActionTile
            title="Mark all fulfilled"
            description="Fulfill every pending pre-order and email customers."
            disabled={preorders.filter((p) => p.status === "pending").length === 0 || busy}
            onClick={onFulfillAll}
            icon={Truck}
            testId="fulfill-all-btn"
          />
        </div>

        <div className="mt-5 pt-5 border-t border-gray-100 flex justify-end">
          <button onClick={onDelete} className="lp-btn-critical" data-testid="delete-campaign-btn">
            <Trash2 size={14} /> Delete campaign
          </button>
        </div>
      </div>

      {/* Referral leaderboard */}
      {campaign.referralDiscountPercent && campaign.referralBonusThreshold ? (
        <div className="lp-card overflow-hidden" data-testid="admin-leaderboard-section">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-amber-600" />
              <h2 className="text-base font-semibold tracking-tight">Referral leaderboard</h2>
            </div>
            <div className="text-xs text-gray-500">
              {campaign.referralDiscountPercent}% off at {campaign.referralBonusThreshold}+ referrals
            </div>
          </div>
          {leaderboard.length === 0 ? (
            <EmptyRow label="No referrals yet. Supporters earn referrals when they share their link." />
          ) : (
            <div className="overflow-x-auto">
              <table className="lp-table" data-testid="admin-leaderboard-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Supporter (masked)</th>
                    <th>Referral code</th>
                    <th>Referrals</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((row, i) => {
                    const eligible = row.referralCount >= campaign.referralBonusThreshold;
                    return (
                      <tr key={row.referralCode}>
                        <td className="font-semibold tabular-nums">#{i + 1}</td>
                        <td>{row.email}</td>
                        <td className="font-mono text-xs">{row.referralCode}</td>
                        <td className="tabular-nums">{row.referralCount}</td>
                        <td>
                          {eligible ? (
                            <span className="lp-badge lp-badge-preorder">Discount earned</span>
                          ) : (
                            <span className="lp-badge lp-badge-voting">
                              {campaign.referralBonusThreshold - row.referralCount} to go
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Voters table */}
      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">Supporters</h2>
          <span className="text-xs text-gray-500">{votes.length} total</span>
        </div>
        {votes.length === 0 ? (
          <EmptyRow label="No supporters yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="lp-table" data-testid="voters-table">
              <thead>
                <tr><th>Email</th><th>Joined</th></tr>
              </thead>
              <tbody>
                {votes.map((v) => (
                  <tr key={v.id}>
                    <td>{v.email}</td>
                    <td>{new Date(v.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pre-orders table */}
      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight">Pre-orders</h2>
          <span className="text-xs text-gray-500">{preorders.length} total</span>
        </div>
        {preorders.length === 0 ? (
          <EmptyRow label={campaign.status === "voting" ? "Pre-orders open once threshold hits." : "No pre-orders yet."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="lp-table" data-testid="preorders-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Order ID</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {preorders.map((p) => (
                  <tr key={p.id}>
                    <td>{p.customerName || "—"}</td>
                    <td>{p.customerEmail}</td>
                    <td className="font-mono text-xs">{p.shopifyOrderId || p.id}</td>
                    <td><StatusBadge status={p.status} /></td>
                    <td>{new Date(p.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const InfoCard = ({ label, value }) => (
  <div className="lp-card p-5">
    <div className="lp-section-label">{label}</div>
    <div className="text-xl font-semibold tracking-tight mt-2">{value}</div>
  </div>
);

const ActionTile = ({ title, description, disabled, onClick, icon: Icon, testId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    className="lp-card p-5 text-left disabled:opacity-50 disabled:cursor-not-allowed hover:border-black transition-all"
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} />
      <div className="text-sm font-semibold">{title}</div>
    </div>
    <p className="text-xs text-gray-500">{description}</p>
  </button>
);

const EmptyRow = ({ label }) => (
  <div className="p-10 text-center text-sm text-gray-500">{label}</div>
);
